import { GRAPHQL_ENDPOINT, } from "./const";

export declare type CatchErrorParams = {
	error: Error;
	system?: string;
	code?: string;
	address?: string;
	headers?: Record<string, string>;
	data?: string;
};

/**
 * Catches and logs errors to the console and records them via GraphQL mutation
 *
 * @param {CatchErrorParams} params - The error catching parameters
 * @param {Error} params.error - The error object to catch and log
 * @param {string} [params.system] - Optional system identifier where the error occurred
 * @param {string} [params.code] - Optional error code
 * @param {string} [params.address] - Optional wallet address associated with the error
 * @param {Record<string, string>} [params.headers] - Optional additional headers for the request
 * @param {string} [params.data] - Optional additional data to record with the error
 * @returns {Promise<void>}
 */
export const catchError = async ({
	error,
	system,
	code,
	address,
	headers = {},
	data,
}: CatchErrorParams): Promise<void> => {
	// Log error to console
	console.error("Error caught:", error.message);
	if (error.stack) {
		console.error("Stack trace:", error.stack);
	}

	try {
		// Record error via GraphQL mutation using fetch
		const response = await fetch(GRAPHQL_ENDPOINT, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				...headers,
			},
			body: JSON.stringify({
				query: `
					mutation record_ui_error(
						$system: String
						$code: String
						$address: String
						$message: String
						$stack: String
						$data: String
					) {
						record_ui_error(
							system: $system
							code: $code
							address: $address
							message: $message
							stack: $stack
							data: $data
						) {
							success
						}
					}
				`,
				variables: {
					system: system || "unknown",
					code: code || error.name,
					address: address || "",
					message: error.message,
					stack: JSON.stringify(error.stack || ""),
					data: data || null,
				},
			}),
		});

		if(!response.ok) {
			throw new Error(`GraphQL request failed: ${response.statusText}`);
		}

		const result = await response.json();
		if (result.errors) {
			throw new Error(`GraphQL errors: ${JSON.stringify(result.errors)}`);
		}
	} catch (mutationError) {
		console.error("Failed to record error via GraphQL:", mutationError);
	}
};
